import React, { useEffect, useState } from "react";
import OfficesDropdown from "../dropdowns/OfficesDropdown";
import CommTypeDropdown from "../dropdowns/CommTypeDropdown";
import "./comm-records-styling.css";
import {
  fetchConversations,
  fetchRecordings,
  fetchCalls,
  fetchVoicemails,
  fetchCallLogs,
} from "../../functions/office/commRecords";
import VoicemailsContainer from "./VoicemailsContainer";
import CallsContainer from "./CallsContainer";
import RecordingsContainer from "./RecordingsContainer";
import TextConversationsContainer from "./TextConversationsContainer";
import Loading from "../loading/Loading";
import LogsContainer from "./LogsContainer";
import { fetchOfficeUsers } from "../../functions/office/officeUserFuncs";

const OfficeCommRecordsContainer = () => {
  const [loading, setLoading] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [selectedCommType, setSelectedCommType] = useState(null);
  const [bothSelected, setBothSelected] = useState(false);
  const [voicemails, setVoicemails] = useState([]);
  const [users, setUsers] = useState([]);
  const [calls, setCalls] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [conversations, setConversations] = useState([
    {
      _id: "",
      customerNumber: "",
    },
  ]);

  const [callLogs, setCallLogs] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 15;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if (currentPage !== 1 && selectedCommType === "voicemails") {
      getVoicemails(currentPage);
    }

    if (currentPage !== 1 && selectedCommType === "allRecordings") {
      getRecordings(currentPage);
    }

    if (currentPage !== 1 && selectedCommType === "calls") {
      getCalls(currentPage);
    }
  }, [currentPage]);

  const fireRefresh = () => {
    setRefresh(refresh + 1);
  };

  const resetState = () => {
    setVoicemails([]);
    setRecordings([]);
    setCalls([]);
    setCurrentPage(1);
    setTotalPages(0);
  };

  const handleOfficeChange = (officeId) => {
    setSelectedOffice(officeId);
    resetState();
  };

  const handleCommTypeChange = (commType) => {
    setSelectedCommType(commType);
    resetState();
  };

  const getVoicemails = async (currentPage = 1) => {
    setLoading(true);
    if (currentPage === 1) {
      setCurrentPage(1);
    }
    const res = await fetchVoicemails(
      selectedOffice,
      itemsPerPage,
      currentPage,
    );
    if (res) {
      const { pagination } = res;
      setTotalPages(Math.ceil(pagination.totalVoicemails / pagination.limit));
      setVoicemails(
        currentPage === 1
          ? res.voicemails
          : (prev) => [...prev, ...res.voicemails],
      );
      setLoading(false);
    } else {
      setLoading(false);
      alert("Error: Failed to fetch voicemails.");
    }
  };

  const getCalls = async (currentPage = 1) => {
    setLoading(true);
    if (currentPage === 1) {
      setCurrentPage(1);
    }
    const res = await fetchCalls(selectedOffice, itemsPerPage, currentPage);

    if (res) {
      const { pagination } = res;
      setTotalPages(Math.ceil(pagination.totalCalls / pagination.limit));
      setCalls(
        currentPage === 1 ? res.calls : (prev) => [...prev, ...res.calls],
      );
      setLoading(false);
    } else {
      setLoading(false);
      alert("Error: Failed to fetch calls.");
    }
  };

  const getRecordings = async (currentPage = 1) => {
    setLoading(true);
    if (currentPage === 1) {
      setCurrentPage(1);
    }
    const res = await fetchRecordings(
      selectedOffice,
      itemsPerPage,
      currentPage,
    );
    if (res) {
      const { pagination } = res;
      setTotalPages(Math.ceil(pagination.totalRecordings / itemsPerPage));
      setRecordings(
        currentPage === 1
          ? res.recordings
          : (prev) => [...prev, ...res.recordings],
      );
      setLoading(false);
    } else {
      setLoading(false);
      alert("Error: Failed to fetch voicemails.");
    }
  };

  const getConversations = async () => {
    const conversationsFromServer = await fetchConversations(selectedOffice);
    setConversations(conversationsFromServer);
    setLoading(false);
  };

  const getCallLogs = async () => {
    const logsFromServer = await fetchCallLogs(selectedOffice, 100);
    setCallLogs(logsFromServer);
    setLoading(false);
  };

  useEffect(() => {
    if (selectedOffice)
      fetchOfficeUsers(selectedOffice).then((users) => {
        setUsers(users);
      });
  }, [selectedOffice]);

  useEffect(() => {
    if (selectedOffice != null && selectedCommType != null) {
      if (selectedCommType === "voicemails") {
        getVoicemails();
      } else if (selectedCommType === "calls") {
        getCalls();
      } else if (selectedCommType === "allRecordings") {
        getRecordings();
      } else if (selectedCommType === "textMessages") {
        getConversations();
      } else if (selectedCommType === "callLogs") {
        getCallLogs();
      }
      setLoading(true);
    }
  }, [selectedOffice, selectedCommType, refresh]);

  return (
    <div className="comm-wrapper">
      <div className="comm-cards-wrapper">
        <div className="comm-half-container">
          <div className="comm-card-content-container">
            <p className="comm-title">Select an Office</p>
            <div style={{ width: "80%" }}>
              <OfficesDropdown handleChange={handleOfficeChange} />
            </div>
          </div>
        </div>
        <div className="comm-half-container">
          <div className="comm-card-content-container">
            <p className="comm-title">Select Communication Record Type</p>
            <div style={{ width: "80%" }}>
              <CommTypeDropdown handleChange={handleCommTypeChange} />
            </div>
          </div>
        </div>
      </div>
      {loading === true ? <Loading /> : null}
      {selectedCommType === "voicemails" && voicemails.length > 0 ? (
        <VoicemailsContainer
          voicemails={voicemails}
          users={users}
          reload={fireRefresh}
        />
      ) : null}
      {selectedCommType === "allRecordings" && recordings.length > 0 ? (
        <RecordingsContainer
          recordings={recordings}
          users={users}
          reload={fireRefresh}
        />
      ) : null}
      {selectedCommType === "calls" && calls.length > 0 ? (
        <CallsContainer calls={calls} reload={fireRefresh} />
      ) : null}
      {selectedCommType === "textMessages" ? (
        <TextConversationsContainer conversations={conversations} />
      ) : null}
      {selectedCommType === "callLogs" ? (
        <LogsContainer calls={callLogs} />
      ) : null}

      {(selectedCommType === "voicemails" ||
        selectedCommType === "calls" ||
        selectedCommType === "allRecordings") &&
        currentPage !== totalPages &&
        !loading &&
        selectedOffice && (
          <div className="flex w-full justify-center py-5">
            <button
              className="px-5 py-3 bg-blue-600 text-white font-semibold text-sm rounded hover:shadow-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:shadow-none"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Load More
            </button>
          </div>
        )}
    </div>
  );
};

export default OfficeCommRecordsContainer;
